export type EmployeeNameFields = {
  name?: string | null
  title?: string | null
  first_name?: string | null
  last_name?: string | null
  title_after?: string | null
  position?: string | null
  role?: string | null
}

export type PersonNameInput = {
  title?: string | null
  firstName?: string | null
  lastName?: string | null
  titleAfter?: string | null
  /** Fallback when structured parts are missing */
  name?: string | null
}

const LEADING_TITLE_RE =
  /^(ing\.|mgr\.|bc\.|bca\.|phdr\.|mudr\.|paeddr\.|judr\.|rndr\.|mvdr\.|ph\.d\.|csc\.|doc\.|prof\.)\s+/i

function clean(s: string | null | undefined): string {
  return (s || '').trim()
}

/** Příjmení vždy VELKÝMI písmeny (cs-CZ), jen pro zobrazení. */
export function formatSurnameDisplay(lastName: string): string {
  const t = lastName.trim()
  if (!t) return ''
  try {
    return t.toLocaleUpperCase('cs-CZ')
  } catch {
    return t.toUpperCase()
  }
}

/**
 * Standardní zobrazení osoby v Intranetu:
 * `{titul} {jméno} {PŘÍJMENÍ} {titul_za}` — příjmení vždy CAPS.
 * Řazení zůstává podle raw příjmení (viz personNameSort).
 */
export function formatPersonName(input: PersonNameInput): string {
  const title = clean(input.title)
  const first = clean(input.firstName)
  const lastRaw = clean(input.lastName)
  const titleAfter = clean(input.titleAfter)

  if (lastRaw || first) {
    const last = lastRaw ? formatSurnameDisplay(lastRaw) : ''
    return [title, first, last, titleAfter].filter(Boolean).join(' ')
  }

  return formatDisplayNameFromString(clean(input.name)) || '—'
}

/**
 * Alias pro zaměstnance (API pole first_name / last_name / title).
 * Stejné pravidlo: titul + jméno + PŘÍJMENÍ.
 */
export function formatEmployeeFullName(emp: EmployeeNameFields): string {
  return formatPersonName({
    title: emp.title,
    firstName: emp.first_name,
    lastName: emp.last_name,
    titleAfter: emp.title_after,
    name: emp.name,
  })
}

/**
 * Když API vrátí jen hotový řetězec (bez parts), zkusíme rozpoznat příjmení
 * a zobrazit ho CAPS. Podporuje:
 * - „Mgr. Serhii Vasyliev“ / „Jan Novák“ (příjmení na konci)
 * - „Vasyliev Serhii, Mgr.“ (školní formát příjmení první + tituly za čárkou)
 */
export function formatDisplayNameFromString(raw: string | null | undefined): string {
  let name = clean(raw)
  if (!name) return ''

  let trailingTitles = ''
  const comma = name.indexOf(',')
  if (comma > 0) {
    trailingTitles = name.slice(comma + 1).trim()
    name = name.slice(0, comma).trim()
  }

  const leadingTitles: string[] = []
  let rest = name
  while (true) {
    const m = rest.match(LEADING_TITLE_RE)
    if (!m) break
    leadingTitles.push(m[1])
    rest = rest.slice(m[0].length).trim()
  }

  const parts = rest.split(/\s+/).filter(Boolean)
  if (parts.length === 0) {
    return [leadingTitles.join(' '), trailingTitles].filter(Boolean).join(' ').trim() || clean(raw)
  }

  // Po čárce: historický formát „Příjmení Jméno, Titul“
  if (trailingTitles && parts.length >= 2) {
    const last = formatSurnameDisplay(parts[0])
    const first = parts.slice(1).join(' ')
    return [trailingTitles, first, last].filter(Boolean).join(' ')
  }

  if (parts.length === 1) {
    return [leadingTitles.join(' '), formatSurnameDisplay(parts[0]), trailingTitles]
      .filter(Boolean)
      .join(' ')
  }

  const last = formatSurnameDisplay(parts[parts.length - 1])
  const first = parts.slice(0, -1).join(' ')
  return [leadingTitles.join(' '), first, last, trailingTitles].filter(Boolean).join(' ')
}

/**
 * Dvousloupcový seznam (Příjmení | Jméno+titul) — příjmení CAPS, řazení dle příjmení.
 */
export function formatPersonListColumns(input: PersonNameInput): {
  surname: string
  given: string
} {
  const title = clean(input.title)
  const first = clean(input.firstName)
  const lastRaw = clean(input.lastName)
  const titleAfter = clean(input.titleAfter)

  if (lastRaw || first) {
    return {
      surname: lastRaw ? formatSurnameDisplay(lastRaw) : '—',
      given: [title, first, titleAfter].filter(Boolean).join(' '),
    }
  }

  const display = formatDisplayNameFromString(clean(input.name))
  if (!display) return { surname: '—', given: '' }

  const bits = display.split(/\s+/).filter(Boolean)
  if (bits.length === 1) return { surname: formatSurnameDisplay(bits[0]), given: '' }
  const surname = bits[bits.length - 1]
  const given = bits.slice(0, -1).join(' ')
  return { surname, given }
}

export function formatEmployeeLastNameLine(emp: EmployeeNameFields): string {
  if (!emp.last_name) return '—'
  const last = formatSurnameDisplay(emp.last_name)
  return emp.title_after ? `${last} ${emp.title_after}` : last
}

export function getEmployeeInitials(emp: EmployeeNameFields): string {
  const first = clean(emp.first_name)
  const last = clean(emp.last_name)
  if (first && last) return `${first[0]}${last[0]}`.toLocaleUpperCase('cs-CZ')
  const name = formatEmployeeFullName(emp)
  const bits = name.split(/\s+/).filter(Boolean)
  if (bits.length >= 2) return `${bits[0][0]}${bits[bits.length - 1][0]}`.toLocaleUpperCase('cs-CZ')
  if (bits.length === 1) return bits[0].slice(0, 2).toLocaleUpperCase('cs-CZ')
  return '?'
}

export function getEmployeePositionLabel(emp: EmployeeNameFields): string | null {
  const pos = emp.position || emp.role
  return pos?.trim() || null
}

/** ISO YYYY-MM-DD → český formát DD.MM.YYYY */
export function formatBirthDateDisplay(iso?: string | null): string {
  if (!iso) return ''
  const m = String(iso).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return String(iso)
  return `${m[3]}.${m[2]}.${m[1]}`
}
